'use client'

import { useState, useEffect } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'

import JobsContent from './JobsContent'
import JobsLanding from './JobsLanding'
import useLocale from '@hooks/client/useLocale'
import miToKm from '@lib/helpers/client/mi-to-km'
import styles from '../jobs.module.scss'

export default function JobsLayout(): JSX.Element {
  const searchParams = useSearchParams()
  const router = useRouter()
  const locale = useLocale()
  
  const locationName = searchParams.get('location_name')
  const locationId = searchParams.get('location_id')
  
  // Show results if we have a location (query can be empty when navigating through category/state/county)
  const hasSearch = locationName || locationId

  // Filter state - initialize from URL params
  const [maxRadius, setMaxRadius] = useState<string>(() => {
    const radius = searchParams.get('max_radius')
    if (radius) {
      const km = Number(radius)
      if (km <= miToKm(10)) return '10'
      if (km <= miToKm(25)) return '25'
      if (km <= miToKm(50)) return '50'
      return '100'
    }
    return '25'
  })

  const [datePosted, setDatePosted] = useState<string>(() => {
    const createdAfter = searchParams.get('created_after')
    if (!createdAfter) return 'anytime'
    // Parse created_after ISO string to determine which option
    try {
      const createdDate = new Date(createdAfter)
      const now = new Date()
      const diffMs = now.getTime() - createdDate.getTime()
      const diffHours = diffMs / (1000 * 60 * 60)
      const diffDays = diffMs / (1000 * 60 * 60 * 24)
      
      if (diffHours <= 24) return '24h'
      if (diffDays <= 7) return '7d'
      if (diffDays <= 30) return '30d'
      return 'anytime'
    } catch {
      return 'anytime'
    }
  })

  const [fullTime, setFullTime] = useState<boolean>(() => {
    return searchParams.get('full_time') !== 'false'
  })

  const [partTime, setPartTime] = useState<boolean>(() => {
    return searchParams.get('part_time') !== 'false'
  })

  const [remote, setRemote] = useState<boolean>(() => {
    return searchParams.get('remote') === 'true'
  })

  const [noExperience, setNoExperience] = useState<boolean>(() => {
    return searchParams.get('no_experience') === 'true'
  })

  const [salaryMin, setSalaryMin] = useState<string>(() => {
    return searchParams.get('salary_min') || '0'
  })

  // Update state when URL params change
  useEffect(() => {
    const radius = searchParams.get('max_radius')
    if (radius) {
      const km = Number(radius)
      if (km <= miToKm(10)) setMaxRadius('10')
      else if (km <= miToKm(25)) setMaxRadius('25')
      else if (km <= miToKm(50)) setMaxRadius('50')
      else setMaxRadius('100')
    }
    
    // Update datePosted from created_after param
    const createdAfter = searchParams.get('created_after')
    if (createdAfter) {
      try {
        const createdDate = new Date(createdAfter)
        const now = new Date()
        const diffMs = now.getTime() - createdDate.getTime()
        const diffHours = diffMs / (1000 * 60 * 60)
        const diffDays = diffMs / (1000 * 60 * 60 * 24)
        
        if (diffHours <= 24) setDatePosted('24h')
        else if (diffDays <= 7) setDatePosted('7d')
        else if (diffDays <= 30) setDatePosted('30d')
        else setDatePosted('anytime')
      } catch {
        setDatePosted('anytime')
      }
    } else {
      setDatePosted('anytime')
    }
    
    setFullTime(searchParams.get('full_time') !== 'false')
    setPartTime(searchParams.get('part_time') !== 'false')
    setRemote(searchParams.get('remote') === 'true')
    setNoExperience(searchParams.get('no_experience') === 'true')
    const salary = searchParams.get('salary_min')
    if (salary) setSalaryMin(salary)
  }, [searchParams])

  const handleUpdate = (): void => {
    // Build new URL params, preserving existing search params
    const params = new URLSearchParams()
    
    // Preserve search params
    const query = searchParams.get('query')
    const locationName = searchParams.get('location_name')
    const locationId = searchParams.get('location_id')
    const locationState = searchParams.get('location_state')
    const locationCounty = searchParams.get('location_county')
    const macro = searchParams.get('macro')
    
    if (query) params.set('query', query)
    if (locationName) params.set('location_name', locationName)
    if (locationId) params.set('location_id', locationId)
    if (locationState) params.set('location_state', locationState)
    if (locationCounty) params.set('location_county', locationCounty)
    if (macro) params.set('macro', macro)
    
    // Update filter params
    params.set('full_time', String(fullTime))
    params.set('part_time', String(partTime))
    params.set('remote', String(remote))
    params.set('no_experience', String(noExperience))
    params.set('sort', searchParams.get('sort') || 'FEATURED')
    params.set('page', '1') // Reset to page 1 when filters change
    
    // Convert miles to km for max_radius
    const radiusKm = miToKm(Number(maxRadius))
    params.set('max_radius', String(radiusKm))
    
    // Handle date posted (created_after)
    if (datePosted !== 'anytime') {
      const now = new Date()
      if (datePosted === '24h') {
        now.setHours(now.getHours() - 24)
      } else if (datePosted === '7d') {
        now.setDate(now.getDate() - 7)
      } else if (datePosted === '30d') {
        now.setDate(now.getDate() - 30)
      }
      params.set('created_after', now.toISOString())
    }
    
    // Handle salary
    if (salaryMin && salaryMin !== '0') {
      params.set('salary_min', salaryMin)
      params.set('salary_type', 'PER_ANNUM')
    }
    
    router.replace(`/${locale}/jobs?${params.toString()}`, { scroll: false })
  }

  const getLocationDisplayName = (): string => {
    return locationName || 'location'
  }

  return (
    <div className={styles.jobsMainGrid}>
      <aside className={styles.jobsSidebar}>
        <div className={styles.jobsSidebarCard}>
          <h2 className={styles.jobsSidebarTitle}>Refine your search</h2>
          <div className={styles.jobsFilterGroup}>
            <label className={styles.jobsFilterLabel}>Distance from {getLocationDisplayName()}</label>
            <div className={styles.jobsSelectWrapper}>
              <select 
                className={styles.jobsSelect}
                value={maxRadius}
                onChange={(e) => setMaxRadius(e.target.value)}
              >
                <option value="10">Within 10 miles</option>
                <option value="25">Within 25 miles</option>
                <option value="50">Within 50 miles</option>
                <option value="100">Within 100 miles</option>
              </select>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.jobsSelectIcon}>
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </div>
          </div>
          <div className={styles.jobsFilterGroup}>
            <label className={styles.jobsFilterLabel}>Date Posted</label>
            <div className={styles.jobsSelectWrapper}>
              <select 
                className={styles.jobsSelect}
                value={datePosted}
                onChange={(e) => setDatePosted(e.target.value)}
              >
                <option value="anytime">Anytime</option>
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              </select>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.jobsSelectIcon}>
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </div>
          </div>
          <div className={styles.jobsFilterGroup}>
            <label className={styles.jobsFilterLabel}>Job Type</label>
            <div className={styles.jobsFilterButtons}>
              <button 
                className={`${styles.jobsFilterButton} ${fullTime ? styles.jobsFilterButtonActive : ''}`}
                onClick={() => setFullTime(!fullTime)}
              >
                Full-time
              </button>
              <button 
                className={`${styles.jobsFilterButton} ${partTime ? styles.jobsFilterButtonActive : ''}`}
                onClick={() => setPartTime(!partTime)}
              >
                Part-time
              </button>
              <button 
                className={`${styles.jobsFilterButton} ${remote ? styles.jobsFilterButtonActive : ''}`}
                onClick={() => setRemote(!remote)}
              >
                Remote
              </button>
              <button 
                className={`${styles.jobsFilterButton} ${noExperience ? styles.jobsFilterButtonActive : ''}`}
                onClick={() => setNoExperience(!noExperience)}
              >
                No Experience
              </button>
            </div>
          </div>
          <div className={styles.jobsFilterGroup}>
            <label className={styles.jobsFilterLabel}>Salary (per annum)</label>
            <div className={styles.jobsSelectWrapper}>
              <select 
                className={styles.jobsSelect}
                value={salaryMin}
                onChange={(e) => setSalaryMin(e.target.value)}
              >
                <option value="0">Any</option>
                <option value="30000">$30,000+</option>
                <option value="50000">$50,000+</option>
                <option value="75000">$75,000+</option>
                <option value="100000">$100,000+</option>
              </select>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.jobsSelectIcon}>
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </div>
          </div>
          <button className={styles.jobsUpdateButton} onClick={handleUpdate}>Update</button>
        </div>
      </aside>
      <div>
        {hasSearch ? <JobsContent /> : <JobsLanding />}
      </div>
    </div>
  )
}
